import { apiClient, getApiOrigin, getApiSessionIdentity } from '../shared/api-client';
import { getDurableItem, setDurableItem, getPersistentItem, setPersistentItem } from '../vrs-auth/storage';
let NativeModulesRef = null;
try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    NativeModulesRef = require('react-native').NativeModules;
}
catch {
    NativeModulesRef = null;
}
const NOTIFICATION_DEVICE_ID_STORAGE_KEY = 'malka_notification_device_id';
function randomId() {
    const globalRef = typeof globalThis !== 'undefined'
        ? globalThis
        : undefined;
    const cryptoRef = globalRef?.crypto;
    if (typeof cryptoRef?.randomUUID === 'function') {
        return cryptoRef.randomUUID();
    }
    return `device-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}
export function getOrCreateNotificationDeviceId() {
    const existing = getPersistentItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY);
    if (existing) {
        return existing;
    }
    const deviceId = randomId();
    setPersistentItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY, deviceId);
    return deviceId;
}
function detectPlatform() {
    const navigatorRef = typeof navigator !== 'undefined' ? navigator : undefined;
    const userAgent = navigatorRef?.userAgent || '';
    if (navigatorRef?.product === 'ReactNative') {
        return 'native';
    }
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
        return 'ios';
    }
    if (/Android/i.test(userAgent)) {
        return 'android';
    }
    return 'web';
}
function currentNotificationPermission() {
    const globalRef = typeof globalThis !== 'undefined'
        ? globalThis
        : undefined;
    return globalRef?.Notification?.permission;
}
function nativeNotificationModule() {
    return NativeModulesRef?.VRSNotifications;
}
function urlBase64ToUint8Array(value) {
    const padding = '='.repeat((4 - value.length % 4) % 4);
    const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}
async function getWebPushSubscription(promptUser, guard) {
    const windowRef = typeof window !== 'undefined'
        ? window
        : undefined;
    const navigatorRef = typeof navigator !== 'undefined'
        ? navigator
        : undefined;
    if (guard && !guard.current())
        return null;
    if (!windowRef
        || typeof navigator === 'undefined'
        || !navigatorRef?.serviceWorker
        || !windowRef.PushManager
        || !windowRef.Notification) {
        return null;
    }
    let permission = windowRef.Notification.permission;
    if (permission === 'default' && promptUser) {
        permission = await windowRef.Notification.requestPermission();
    }
    if (guard && !guard.current())
        return null;
    if (permission !== 'granted') {
        return null;
    }
    const config = await apiClient.get('/api/notifications/web-push/public-key', { retryAuth: false, signal: guard?.signal });
    if (guard && !guard.current())
        return null;
    if (config.error || config.status !== 200 || config.data?.ready !== true || typeof config.data.publicKey !== 'string' || !config.data.publicKey) {
        return null;
    }
    const registration = await navigatorRef.serviceWorker.ready;
    if (guard && !guard.current())
        return null;
    const existing = await registration.pushManager.getSubscription();
    if (guard && !guard.current())
        return null;
    if (existing) {
        return existing;
    }
    return registration.pushManager.subscribe({
        applicationServerKey: urlBase64ToUint8Array(config.data.publicKey),
        userVisibleOnly: true
    });
}
async function nativePushOptions(promptUser, guard) {
    const module = nativeNotificationModule();
    // Old bridges expose only the interactive method. Never call it on reconnect.
    const request = promptUser ? module?.requestPushToken : module?.getPushToken;
    if (guard && !guard.current())
        return null;
    if (!request) {
        return null;
    }
    const result = await request.call(module);
    if (guard && !guard.current())
        return null;
    if (!result?.token) {
        return null;
    }
    return {
        appId: result.appId,
        platform: result.platform || detectPlatform(),
        pushProvider: result.pushProvider || (result.platform === 'ios' ? 'apns' : 'fcm'),
        pushToken: result.token
    };
}
async function webPushOptions(promptUser, guard) {
    const subscription = await getWebPushSubscription(promptUser, guard);
    if (!subscription) {
        return null;
    }
    return {
        platform: 'web',
        pushProvider: 'web-push',
        pushToken: JSON.stringify(subscription.toJSON())
    };
}
/** Automatic refresh preserves the server preference; only Settings supplies a reviewed revision. */
export function registerCurrentNotificationDevice(options = {}) {
    const session = getApiSessionIdentity();
    return new Promise(resolve => {
        const controller = new AbortController();
        let settled = false;
        const failed = () => ({ data: null, error: 'Notification device registration is not confirmed', status: 0 });
        const finish = (response) => {
            if (settled)
                return;
            settled = true;
            clearTimeout(timer);
            controller.abort();
            resolve(response);
        };
        const timer = setTimeout(() => finish(failed()), 20000);
        const current = () => !settled && Boolean(session) && session === getApiSessionIdentity();
        const guard = { current, signal: controller.signal };
        void (async () => {
            if (!current()) {
                finish(failed());
                return;
            }
            const stored = await getDurableItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY);
            if (!current()) {
                finish(failed());
                return;
            }
            const cached = getNotificationDeviceId();
            if (stored && cached && stored !== cached) {
                finish(failed());
                return;
            }
            const deviceId = stored || cached || getOrCreateNotificationDeviceId();
            if (deviceId.length < 8 || deviceId.length > 160) {
                finish(failed());
                return;
            }
            setPersistentItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY, deviceId);
            await setDurableItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY, deviceId);
            if (!current() || await getDurableItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY) !== deviceId || !current()) {
                finish(failed());
                return;
            }
            const providerOptions = options.pushToken ? options : await nativePushOptions(false, guard)
                || await webPushOptions(false, guard) || options;
            if (!current()) {
                finish(failed());
                return;
            }
            const ringStop = ringStopMetadata();
            const response = await apiClient.post('/api/notifications/devices', {
                appId: providerOptions.appId, deviceId, enabled: true,
                metadata: {
                    notificationPermission: currentNotificationPermission(),
                    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
                    ...ringStop
                },
                platform: providerOptions.platform || detectPlatform(),
                pushProvider: providerOptions.pushProvider || (providerOptions.pushToken ? 'unknown' : 'local'),
                pushToken: providerOptions.pushToken, tenantId: options.tenantId
            }, { signal: controller.signal, retryAuth: false });
            finish(current() ? response : failed());
        })().catch(() => finish(failed()));
    });
}
/**
 * A native build that stops rings says so in its registration, and learns
 * the API origin a ringing phone checks its ring against.
 */
function ringStopMetadata() {
    const module = nativeNotificationModule();
    const version = Number(module?.ringStopVersion);
    if (!module || !Number.isInteger(version) || version < 1)
        return {};
    try {
        void module.setRingApiOrigin?.call(module, getApiOrigin())?.catch?.(() => undefined);
    }
    catch {
        // The origin is best effort: the ring still ends at its deadline.
    }
    return { ringStop: version };
}
/**
 * The iPhone's APNs token for silent ring cancels, registered beside the
 * PushKit call token under the same device id. It needs no notification
 * permission and never prompts; builds without getBackgroundPushToken fall
 * back to the ordinary alert-token registration (permission-gated).
 */
export async function registerRingStopToken() {
    const module = nativeNotificationModule();
    const session = getApiSessionIdentity();
    const current = () => Boolean(session) && session === getApiSessionIdentity();
    if (!module?.getBackgroundPushToken)
        return registerAlertNotificationToken(false);
    const deviceId = await getDurableItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY) || getNotificationDeviceId();
    if (!deviceId || !current())
        return false;
    let result;
    try {
        result = await module.getBackgroundPushToken.call(module);
    }
    catch {
        return false;
    }
    if (!result?.token || !current())
        return false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
        const response = await apiClient.post('/api/notifications/devices', {
            appId: result.appId, deviceId, enabled: true, platform: 'ios', pushProvider: 'apns',
            pushToken: result.token, pushType: 'alert'
        }, { retryAuth: false, signal: controller.signal });
        return current() && !response.error && response.status >= 200 && response.status < 300;
    }
    finally {
        clearTimeout(timer);
    }
}
/** Acquire a provider token without registering an account. Settings checks ownership afterwards. */
export async function requestNotificationPushOptions(guard) {
    return await nativePushOptions(true, guard) || await webPushOptions(true, guard);
}
/**
 * Meeting reminders need ordinary notifications. iOS keeps those on a second
 * token beside the PushKit call token, registered here under the same device
 * id; `prompt` (turning reminders on) may show the system prompt, reconnects
 * never do. Android's one FCM token already serves both, so asking there only
 * requests the notification permission and refreshes that registration.
 */
export async function registerAlertNotificationToken(prompt = false) {
    const module = nativeNotificationModule();
    const session = getApiSessionIdentity();
    const current = () => Boolean(session) && session === getApiSessionIdentity();
    if (!module || !current())
        return false;
    if (!module.getAlertPushToken && !module.requestAlertPushToken) {
        if (!prompt || !module.requestPushToken)
            return false;
        const response = await registerCurrentNotificationDevice(await nativePushOptions(true) || {});
        return !response.error;
    }
    const request = prompt ? module.requestAlertPushToken : module.getAlertPushToken;
    const deviceId = await getDurableItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY) || getNotificationDeviceId();
    if (!request || !deviceId || !current())
        return false;
    let result;
    try {
        result = await request.call(module);
    }
    catch {
        return false;
    }
    if (!result?.token || !current())
        return false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
        const response = await apiClient.post('/api/notifications/devices', {
            appId: result.appId, deviceId, enabled: true, platform: 'ios', pushProvider: 'apns',
            pushToken: result.token, pushType: 'alert'
        }, { retryAuth: false, signal: controller.signal });
        return current() && !response.error && response.status >= 200 && response.status < 300;
    }
    finally {
        clearTimeout(timer);
    }
}
export function getNotificationDeviceId() {
    return getPersistentItem(NOTIFICATION_DEVICE_ID_STORAGE_KEY);
}
export async function getNotificationPermissionState() {
    const module = nativeNotificationModule();
    if (!module?.getNotificationPermission)
        return 'unknown';
    try {
        const state = await module.getNotificationPermission.call(module);
        return state === 'granted' || state === 'denied' || state === 'default' ? state : 'unknown';
    }
    catch {
        return 'unknown';
    }
}
/**
 * Shows the system notification question, for a person who tapped Turn On
 * (client Settings → Notifications). iOS asks for alerts, Android 13+ for
 * POST_NOTIFICATIONS. The token that comes back is not registered here:
 * device registration does that. Returns the permission after the question.
 */
export async function requestNotificationPermission() {
    const module = nativeNotificationModule();
    const request = module?.requestAlertPushToken || module?.requestPushToken;
    if (request) {
        try {
            await request.call(module);
        }
        catch {
            // The read below says what the person chose.
        }
    }
    return getNotificationPermissionState();
}
// One system prompt per app run: going Available again after a "Don't allow"
// only refreshes the registration (Android would otherwise ask every time).
let offerPromptAsked = false;
/**
 * Malka Terp going Available: make sure an interpreter offer can reach this
 * phone while it is locked or in the background. Android creates the
 * "Interpreter requests" channel first (offers name it), then both platforms
 * register the ordinary-notification token: iOS its APNs alert token beside
 * the PushKit call token, Android its FCM token. With `prompt`, the first
 * call of a run may show the system prompt and later ones do not, unless the
 * interpreter asked for it (`userAsked`: Settings → Turn on notifications).
 *
 * @returns {Promise<boolean>} Whether a registration was confirmed. What
 * actually reaches the phone is read back from the server (Settings).
 */
export async function registerOfferNotifications(prompt = false, userAsked = false) {
    const module = nativeNotificationModule();
    if (!module)
        return false;
    try {
        await module.prepareOfferNotifications?.call(module);
    }
    catch {
        // The channel is optional: Android shows offers in its default channel without it.
    }
    const ask = prompt && (userAsked || !offerPromptAsked);
    if (ask)
        offerPromptAsked = true;
    // Android keeps one FCM token for calls and alerts: without a prompt,
    // refresh that registration (it carries a token once notifications are allowed).
    if (!ask && !module.getAlertPushToken && !module.requestAlertPushToken) {
        const response = await registerCurrentNotificationDevice();
        return !response.error;
    }
    return registerAlertNotificationToken(ask);
}
/**
 * Takes the offer notification off the lock screen and notification list once
 * the app is showing (or has finished with) the offer itself. Best effort: a
 * notification left behind only opens the app, which shows the current state.
 */
export function clearOfferNotifications() {
    const module = nativeNotificationModule();
    if (!module?.clearOfferNotifications)
        return;
    try {
        void module.clearOfferNotifications.call(module).catch(() => undefined);
    }
    catch {
        // Nothing to clear on a build without the method.
    }
}
/** Test hook: the once-per-run prompt starts fresh. */
export function resetOfferNotificationPromptForTests() {
    offerPromptAsked = false;
}
